import { z } from 'zod';
import { actorKindSchema } from './audit.js';
import { lizenzBauartSchema, lizenzStufeSchema, lizenzZustandSchema } from './lizenz.js';
import { centAmount, isoDate, uuid } from './posting.js';
/**
 * Die Antwortverträge von `/health` und den Systemendpunkten —
 * `V-vertrag3` (2026-08-29).
 *
 * `apps/api/src/health/health.controller.ts` und
 * `apps/api/src/system/status.types.ts`/`status.service.ts` führten ihre
 * Antwortformen bislang ohne Verbindung zu `@fameacc/contracts`; der
 * OpenAPI-Erzeuger blieb an `GET /health`, `GET /api/v1/system/status` und
 * `GET /api/v1/system/fehler` deshalb `[UNGEKLÄRT]`.
 *
 * **`/health` trägt genau ein Feld, mit Absicht** (siehe der Kopfkommentar
 * von `health.controller.ts`): Der Fehlerfall (`503`, `{"status":"error"}`)
 * ist keine zweite Form derselben Route, sondern eine
 * `ServiceUnavailableException` — dieser Vertrag beschreibt ausschliesslich
 * die erfolgreiche Antwort, dieselbe Grenze wie bei jeder anderen Route
 * dieses Erzeugers.
 */
export const healthResponseSchema = z.object({ status: z.literal('ok') }).strict();
/**
 * Die Antwort von `/health/live` — Liveness statt Bereitschaft, seit
 * `OKD-probes` (2026-09-03).
 *
 * Anders als `healthResponseSchema` trägt sie drei Felder statt eines, weil
 * die Frage eine andere ist: nicht «kann der Dienst bedienen», sondern «lebt
 * der Prozess» — und genau deshalb ohne jede Abhängigkeit beantwortet
 * (`apps/api/src/health/health.controller.ts`, `liveness()`). `uptimeSeconds`
 * und `version` sind keine Diagnose im Sinn des Kopfkommentars dort — sie
 * verraten nichts über Datenbank oder Redis, nur, wie lange dieser eine
 * Prozess schon läuft und welche Fassung er ist.
 */
export const healthLiveResponseSchema = z
    .object({
    status: z.literal('live'),
    uptimeSeconds: z.number(),
    version: z.string(),
})
    .strict();
/**
 * Die Lage eines Bereichs — `apps/api/src/system/status.types.ts`, `Lage`.
 *
 * `unbekannt` ist der wichtigste der vier Werte: Ein Job ohne persistente
 * Spur ist nicht gesund, er ist unbeobachtet.
 */
export const lageSchema = z.enum(['gruen', 'gelb', 'rot', 'unbekannt']);
/**
 * Ein Infrastrukturdienst, jetzt angefasst — `DienstLage`.
 *
 * **`'belegablage'` seit `I-ablageeigentum` (2026-09-05), additiv wie jeder
 * bisherige Zuwachs eines `schluessel`-Enums auf dieser Seite** (`jobLageSchema`
 * hat denselben Weg mehrfach genommen — `'fints-takt'`, `'zahldienst-frisch'`,
 * `'aborechnungen'`). Anlass: ein root-eigener Ablagebaum liess jeden
 * PayPal-/Mollie-Abruf 37 Stunden lang unbemerkt an `EACCES` scheitern, weil
 * keine Zeile der Statusseite je die SCHREIBBARKEIT der Ablage selbst prüfte
 * — nur, was aus ihr an Fachzeilen folgte.
 */
export const dienstLageSchema = z
    .object({
    /** Stabiler Schlüssel für die Oberfläche. Der Name wird übersetzt, dieser nicht. */
    schluessel: z.enum(['datenbank', 'redis', 'belegablage']),
    name: z.string(),
    lage: lageSchema,
    /** Die gemessene Antwortzeit in Millisekunden. `null`, wenn keine Antwort kam. */
    dauerMs: z.number().nullable(),
    hinweis: z.string().nullable(),
})
    .strict();
/** Eine Zeile unterhalb eines Jobs — `JobZeile`. */
export const jobZeileSchema = z
    .object({
    bezeichnung: z.string(),
    lage: lageSchema,
    zuletztAm: z.string().nullable(),
    hinweis: z.string().nullable(),
})
    .strict();
/** Ein Hintergrundlauf, gemessen an der Spur, die er hinterlässt — `JobLage`. */
export const jobLageSchema = z
    .object({
    /**
     * `'fints-takt'` seit `C-fintstakt` und `'zahldienst-frisch'` seit
     * `C-zahltakt` (beide 2026-09-01, Owner-Auftraege desselben Abends:
     * FinTS im Systemstatus sichtbar; PayPal/Mollie stuendlich). Beide
     * additiv -- zwei neue Enum-Werte, kein Feld aendert Form. Beim Stapeln
     * (sammel109) vereint, wie von beiden Zweigen als Naht benannt.
     *
     * `'aborechnungen'` seit `I-abojoblauf` (2026-09-02) -- die fuenfte
     * Schiene, ebenso additiv: ein weiterer Enum-Wert, kein Feld aendert
     * Form. Sie ist der erste Job, der seinen Platz VOR seinem Schreiber
     * bekommt: Der Generator (`R-abolauf`) ist noch nicht gebaut, und die
     * Zeile meldet bis dahin `unbekannt` -- die richtige Lesart, nicht eine
     * Luecke.
     *
     * `'zahlungszuordnung'` seit `ZZ-automatiktakt` (2026-09-07) -- die
     * sechste Schiene, wieder additiv: ein weiterer Enum-Wert, kein Feld
     * aendert Form. Sie ist der naechtliche Takt des reinen Volltreffers
     * (`D-14`), der bis dahin nur ueber `pnpm zahlung:automatik -- --run`
     * von Hand lief. Der Name steht woertlich an drei Orten: hier, in
     * `JOB_ZAHLUNGSZUORDNUNG` (`apps/api/src/system/herzschlag.ts`) und im
     * `CHECK` von `job_lauf` (Migration `0182`).
     *
     * `'lizenz'` seit `LZ-grund` (2026-09-29, Spec Lizenzierung `D-34`) --
     * die Schiene `lizenzTakt()`, wieder additiv. Wie `'aborechnungen'`
     * bekommt sie ihren Platz VOR ihrem Schreiber (`LZ-erneuerung`) und
     * VOR ihrer Zeile im Statusdienst; seit `LZ-erneuerung` (2026-09-30)
     * steht sie als siebter Job in jeder Antwort. Der Name steht woertlich in `JOB_LIZENZ`
     * (`apps/api/src/system/herzschlag.ts`) und im `CHECK` von `job_lauf`
     * (Migration `0246`).
     *
     * `'mail-abruf'` und `'workflow-laeufer'` seit `I-basis-em-wf`
     * (2026-11-04, Specs E-Mail-Eingang `D-9` und Workflows `D-15`) -- der
     * achte und neunte Job, wieder additiv und wie `'lizenz'` VOR ihren
     * Schreibern (`EM-abruf`, `WF-laeufer`); bis dahin meldet die Zeile
     * `unbekannt`. Die Namen stehen woertlich in `JOB_MAIL_ABRUF` und
     * `JOB_WORKFLOW_LAEUFER` (`apps/api/src/system/herzschlag.ts`) und im
     * `CHECK` von `job_lauf` (Migration `0352`).
     */
    schluessel: z.enum([
        'zahldienstabruf',
        'suchindex-nachlauf',
        'fints-takt',
        'zahldienst-frisch',
        'aborechnungen',
        'zahlungszuordnung',
        'lizenz',
        'mail-abruf',
        'workflow-laeufer',
    ]),
    name: z.string(),
    /** Der Soll-Takt in Worten. */
    takt: z.string(),
    lage: lageSchema,
    zuletztAm: z.string().nullable(),
    hinweis: z.string().nullable(),
    /** Die Tabelle, aus der die Aussage stammt. */
    spur: z.string(),
    /** Was diese Messung **nicht** sagt. */
    grenze: z.string(),
    zeilen: z.array(jobZeileSchema),
})
    .strict();
/**
 * Was ein Durchgang eines Hintergrundlaufs in `job_lauf.ergebnis` hinterlassen
 * kann — dieselbe Liste wie `job_lauf_ergebnis_bekannt` (Migration `0246`).
 *
 * `'angehalten'` seit `LZ-grund` (2026-09-29, Spec Lizenzierung 7.2, `D-34`):
 * Eine Schiene, die im Nur-Lese-Betrieb nicht anlaufen darf, schreibt ihren
 * Durchgang so und nicht als `'leer'` — „lief, fand nichts" und „durfte
 * nicht" sind zwei verschiedene Aussagen. Die Quelle für den Typ
 * `JobErgebnis` in `apps/api/src/system/herzschlag.ts`; eine zweite,
 * nachgeschriebene Vereinigung dort meldete `pnpm api:enums:check`.
 */
export const jobErgebnisSchema = z.enum(['ok', 'leer', 'fehler', 'angehalten']);
/** Ein gescheiterter Anmeldeversuch — `Anmeldefehlversuch`. */
export const anmeldefehlversuchSchema = z
    .object({
    am: z.string(),
    /** `auth.login_failed` oder `auth.totp_failed`. */
    art: z.string(),
    /** `unknown_email`, `bad_password`, `locked`, `disabled` — oder `null` beim TOTP. */
    grund: z.string().nullable(),
    /** Die Adresse, maskiert. Nie vollständig. */
    adresse: z.string(),
    ip: z.string().nullable(),
})
    .strict();
/** Die Anmeldeversuche, die nicht durchkamen — `AnmeldungenLage`. */
export const anmeldungenLageSchema = z
    .object({
    lage: lageSchema,
    fensterStunden: z.number(),
    imFenster: z.number().int().nonnegative(),
    inSiebenTagen: z.number().int().nonnegative(),
    letzte: z.array(anmeldefehlversuchSchema),
})
    .strict();
/** Eine Anfrage, die mit einem Fehlerstatus endete — `Fehleranfrage`. */
export const fehleranfrageSchema = z
    .object({
    am: z.string(),
    methode: z.string(),
    pfad: z.string(),
    status: z.number().int(),
    dauerMs: z.number(),
})
    .strict();
/** Die Schnittstelle, gezählt über das Zugriffsprotokoll — `ZugriffeLage`. */
export const zugriffeLageSchema = z
    .object({
    lage: lageSchema,
    fensterStunden: z.number(),
    anfragen: z.number().int().nonnegative(),
    fehler4xx: z.number().int().nonnegative(),
    fehler5xx: z.number().int().nonnegative(),
    letzteFehler: z.array(fehleranfrageSchema),
})
    .strict();
/**
 * Die Herkunft der laufenden Auslieferung — `FassungLage`, seit `DP-version`
 * (2026-09-02, Spec-Entscheidung `D-9`).
 *
 * **Der sechste Bereich der Statusantwort, und ausdruecklich kein eigener
 * Endpunkt.** Anforderung Nr. 27 nennt woertlich einen «local-only endpoint»;
 * ein zusaetzlicher, nur lokal erreichbarer Weg waere ein zweiter Ort fuer
 * dieselbe Auskunft — und der, auf den niemand schaut. `system:status` ist
 * bereits authentifiziert, ist als wachsende Antwort angelegt und wird ohnehin
 * gelesen.
 *
 * **`version` und `gitSha` kommen aus der zur BAUZEIT erzeugten Datei**
 * (`apps/api/src/version.generated.ts`), nicht aus der Umgebung: Eine
 * Umgebungsvariable ist zur Laufzeit setzbar, und ein Abbild, das seinen
 * eigenen Herkunfts-SHA daraus liest, kann ueber seine Herkunft luegen
 * (Anforderung Nr. 26).
 *
 * **`imageDigest` ist die eine Ausnahme und deshalb nullable.** Der Digest ist
 * der Hash ueber die Schichten des Abbilds; eine Datei im Abbild, die ihn
 * enthaelt, ist Teil einer Schicht und aendert ihn (`D-10`). Er kommt aus der
 * Quadlet-Unit — und im Entwicklungsbetrieb gibt es ihn schlicht nicht. `null`
 * ist dort die Wahrheit; eine erfundene Zeichenkette waere eine Auskunft ueber
 * ein Abbild, das nicht laeuft.
 */
export const fassungLageSchema = z
    .object({
    /** Die annotierte Marke `v<x>.<y>.<z>` — oder ein Entwicklungsstand `0.0.0-dev+<kurzsha>`. */
    version: z.string(),
    /** Der volle Commit, 40 Hexziffern. */
    gitSha: z.string(),
    /** Der Digest des laufenden Abbilds, oder `null` im Betrieb ohne Container. */
    imageDigest: z.string().nullable(),
})
    .strict();
/**
 * Die Stufe der EU-B2C-Schwelle (§ 3a Abs. 5 UStG) — `EuB2cStufe`, seit
 * `ST-osswaechter` (2026-09-05, Auftraggeber-Entscheidung nach der Messung aus
 * `ST-ossmessung`: unter der Schwelle bleiben, aber ab 80 % warnen, damit die
 * OSS-Umstellung nicht vom Umsatz überrascht wird).
 *
 * Vier Stufen, nicht drei — `unbekannt` gehört dazu, aus demselben Grund wie
 * bei `Lage`: Misslingt die Messung (die Datenbank antwortet nicht), ist der
 * Umsatz nicht „in Ordnung", er ist unbeobachtet, und eine Statusseite, die
 * das grün malt, beruhigt statt zu melden (Regel dieser ganzen Datei, siehe
 * `status.service.ts`). Von den übrigen drei ist `gruen` die einzige echte
 * Ampelfarbe: Ein Umsatz, der die Schwelle **erreicht**, ist eine andere
 * Nachricht als einer, der sie **überschritten** hat — die erste ist eine
 * Warnung vor einer bevorstehenden Umstellung, die zweite eine
 * Zusicherungsverletzung des Bestimmungslandprinzips, wenn deutsche statt der
 * Steuer des Bestimmungslands weiterberechnet wird.
 */
export const euB2cStufeSchema = z.enum(['gruen', 'warnung', 'ueberschritten', 'unbekannt']);
/**
 * Der laufende EU-B2C-Nettoumsatz des Kalenderjahres gegen die 10.000-€-Schwelle
 * — `EuB2cSchwelle`, seit `ST-osswaechter`.
 *
 * **`jahr` ist das LAUFENDE Kalenderjahr**, nicht ein wählbarer Parameter:
 * Die Zusicherung, die geprüft wird, ist § 3a Abs. 5 Satz 3 UStG — die
 * EU-weite Zusammenrechnung ÜBER EIN JAHR — und die einzige Frage, die der
 * Systemstatus beantwortet, ist, wo dieses eine laufende Jahr gerade steht.
 * Eine retrospektive Auswertung über mehrere Jahre bleibt `pnpm steuer:eu-b2c`
 * vorbehalten (`scripts/messung-eu-b2c.mjs`).
 *
 * **`netto` und `schwelle` sind `Money`, `anteil` ist eine reine Verhältniszahl
 * als Zeichenkette** — Hausregel 1 gilt für Beträge, nicht für eine aus ihnen
 * abgeleitete Quote; sie wird trotzdem nie über `number` gerechnet
 * (`packages/money`), damit dieselbe Rundung gilt wie überall sonst.
 */
export const euB2cSchwelleSchema = z
    .object({
    jahr: z.number().int(),
    netto: centAmount,
    schwelle: centAmount,
    /** Prozentwert als Zeichenkette, z. B. `"82.41"` — kein `%`-Zeichen, das ist Sache der Anzeige. */
    anteil: z.string(),
    stufe: euB2cStufeSchema,
})
    .strict();
/**
 * Der Zustand einer Geheimnisform — `GeheimnisformZustand`, seit
 * `I-geheimnisform` (2026-09-12).
 *
 * **Drei Zustände, kein `unbekannt`.** Anders als `Lage` gibt es hier keinen
 * vierten Wert: Die Form eines Umgebungswerts ist immer feststellbar, ohne
 * Netzwerk und ohne Datenbank — es gibt nichts, das scheitern könnte, ausser
 * der Form selbst. `ok` — gültig. `fehlt` — nicht gesetzt, leer oder nur
 * Leerraum (für jeden Schlüssel gleich, `E-1840`). `form` — nicht leer, aber
 * falsch: falsche Byte-Länge nach base64-Dekodierung, oder Leerraum am Rand
 * bei einem Klartextwert.
 */
export const geheimnisformZustandSchema = z.enum(['ok', 'fehlt', 'form']);
/**
 * Eine Zeile der Geheimnisform-Prüfung — `Geheimnisform`, seit
 * `I-geheimnisform` (2026-09-12, Auftraggeber-Anlass: `WEBHOOK_SECRET_KEY`
 * falsch gesetzt, nur faul beim ersten Gebrauch gemeldet, kein sichtbares
 * Zeichen dafür, ob ein Pod nach `oc rollout restart` den neuen Stand hält).
 *
 * **Die Regel steht in `apps/api/src/system/geheimnisform.ts`.** Diese Zeile
 * beschreibt nur die FORM der Antwort, nicht ihre Herleitung.
 *
 * **`fingerabdruck` ist die einzige Auskunft über den Wert, und sie ist
 * absichtlich schwach.** Sechs Hexziffern aus `sha256(wert)`, nie mehr, nie
 * der Wert selbst, nie eine Länge jenseits dessen, was `hinweis` bei einem
 * Formfehler ohnehin schon nennt — nur bei `zustand: 'ok'`. Ein
 * Fingerabdruck über einen falschen Wert wäre eine Auskunft über einen Wert,
 * den niemand haben soll; `null` ist deshalb der Regelfall bei `fehlt` und
 * `form`, nicht eine Lücke.
 */
export const geheimnisformSchema = z
    .object({
    /** Der Name der Umgebungsvariablen, z. B. `WEBHOOK_SECRET_KEY`. */
    schluessel: z.string(),
    /** Ob ein fehlender Wert ein Befund ist — `false` für PayPal, Mollie und `SMTP_*`. */
    pflicht: z.boolean(),
    zustand: geheimnisformZustandSchema,
    /** Menschenlesbar, nie der Wert. */
    hinweis: z.string(),
    /** Sechs Hexziffern von `sha256(wert)`, nur bei `zustand: 'ok'`. */
    fingerabdruck: z.string().nullable(),
})
    .strict();
/**
 * Die Zeile „Lizenz" im Systemstatus — Spec Lizenzierung
 * (`docs/superpowers/specs/2026-09-29-lizenzierung-design.md`, Abschnitt 8
 * und 10.2, `E-1563`), seit `LZ-erneuerung` (2026-09-30).
 *
 * **Die Lage:** `gruen` bei `aktiv`, `gelb` bei `warnung`, `rot` sonst (der
 * Nur-Lese-Betrieb). Bis `LZ-scharf` (2026-10-13) war die meldende Stufe gelb
 * und ab 45 Tagen vor `meldendBis` rot; seither ist `stufe` fest `scharf` und
 * `meldendBis` fest `null` (`D-52`). `unbekannt`, solange der
 * Prozess keinen Stand lesen konnte. **Ohne Kennungen**, wie das
 * Hinweisband: Lizenz- und Installationskennung stehen auf der Lizenzseite
 * unter `lizenz:verwalten`.
 */
export const lizenzStatusSchema = z
    .object({
    lage: lageSchema,
    /** `null`, solange dieser Prozess keinen Stand lesen konnte. */
    zustand: lizenzZustandSchema.nullable(),
    stufe: lizenzStufeSchema,
    bauart: lizenzBauartSchema,
    /** Ganze Tage bis zum Ende der Freischaltung; `null` ohne geltende. */
    restTage: z.number().int().nullable(),
    /** Der letzte Tag der meldenden Stufe — seit `LZ-scharf` immer `null`. */
    meldendBis: isoDate.nullable(),
    /** Wann zuletzt eine Freischaltung angenommen wurde, ISO-8601; `null`: nie. */
    letzteFreischaltungAm: z.string().nullable(),
    hinweis: z.string().nullable(),
})
    .strict();
/**
 * Ein Betriebsfehler — getrennt von den Lagen der Bereiche, weil er keine
 * Messung ist, sondern ein Fehler, den ein Prozess an sich selbst feststellt.
 * Heute genau einer: die Zeitmarke der Lizenzprüfung ist nicht schreibbar
 * (Spec Lizenzierung 5.2 Regel 3, `E-1566`) — ausdrücklich **nicht** in der
 * Zeile „Lizenz", denn er ist kein Lizenzzustand. Eine leere Liste heisst:
 * keiner.
 */
export const betriebsfehlerSchema = z
    .object({
    schluessel: z.enum(['lizenz-zeitmarke']),
    /** Seit wann, ISO-8601 (erster gescheiterter Versuch); `null`, wenn unbekannt. */
    seit: z.string().nullable(),
    meldung: z.string(),
})
    .strict();
/**
 * Eine Lage der Datensicherung — Auftrag `DS-nachweis`, Task 4 des Plans
 * `docs/superpowers/plans/2026-10-06-datensicherung-umsetzung.md`, Spec
 * `docs/superpowers/specs/2026-10-06-datensicherung-design.md` Abschnitt 13
 * (`D-19`, `D-43`, `E-1769`).
 *
 * Je Lage eine Ampel, der letzte Erfolg und ein Hinweis. **`zuletztAm` ist der
 * letzte ERFOLG und nicht der letzte Versuch** — ein gescheiterter Lauf färbt
 * die Lage (rot), verschiebt aber nie den Zeitpunkt, an dem zuletzt etwas
 * gesichert wurde.
 */
export const datensicherungLageSchema = z
    .object({
    lage: lageSchema,
    /** Der letzte erfolgreiche Lauf dieser Art, ISO-8601; `null`: noch keiner. */
    zuletztAm: z.string().nullable(),
    hinweis: z.string().nullable(),
})
    .strict();
/**
 * Die Zeile „Datensicherung" des Systemstatus: drei Lagen — die lokale
 * Sicherung (`cron.sh`), die Auslagerung (Schiene im `worker`) und die
 * Prüfung des Ziels — und die schlechteste von ihnen als `lage`.
 *
 * **Kein Eintrag in `jobLageSchema` und kein `job_lauf`** (`D-19`): Der
 * Herzschlag der Sicherung ist die Laufliste `sicherung_lauf` selbst, und ein
 * Job dort verlangte eine Neufassung der Bedingung `job_lauf_job_bekannt`.
 * Die Zeile liest die Sicht `sicherung_offen` (Migration `0280`) — dieselbe
 * Quelle wie `pnpm sicherung:check`.
 */
export const datensicherungStatusSchema = z
    .object({
    lage: lageSchema,
    lokal: datensicherungLageSchema,
    auslagerung: datensicherungLageSchema,
    pruefung: datensicherungLageSchema,
})
    .strict();
/** Die ganze Antwort — `GET /api/v1/system/status`. */
export const systemStatusSchema = z
    .object({
    erhobenAm: z.string(),
    /**
     * Die Herkunft der laufenden Auslieferung. **Additiv** hinzugekommen
     * (`DP-version`): ein neues Feld bricht keinen Aufrufer, und
     * `pnpm api:spec:check` haelt genau das gegen `HEAD`.
     */
    fassung: fassungLageSchema,
    dienste: z.array(dienstLageSchema),
    jobs: z.array(jobLageSchema),
    anmeldungen: anmeldungenLageSchema,
    zugriffe: zugriffeLageSchema,
    /**
     * Der siebte Bereich, seit `ST-osswaechter` (2026-09-05). **Additiv**
     * wie `fassung`: ein neues Feld bricht keinen Aufrufer.
     */
    euB2cSchwelle: euB2cSchwelleSchema,
    /**
     * Der achte Bereich, seit `I-geheimnisform` (2026-09-12). **Additiv**
     * wie `fassung` und `euB2cSchwelle`: ein neues Feld bricht keinen
     * Aufrufer. Eine Angabe wie `fassung`, keine Messung wie `dienste` — es
     * gibt nichts anzufassen, nur eine Zeichenkette auf ihre Form zu prüfen.
     */
    geheimnisse: z.array(geheimnisformSchema),
    /**
     * Der neunte Bereich, seit `LZ-erneuerung` (2026-09-30). **Additiv** wie
     * `fassung`: ein neues Feld bricht keinen Aufrufer.
     */
    lizenz: lizenzStatusSchema,
    /** Seit `LZ-erneuerung` (2026-09-30), additiv — leer, solange es keinen gibt. */
    betriebsfehler: z.array(betriebsfehlerSchema),
    /**
     * Der zehnte Bereich, seit `DS-nachweis` (2026-10-07). **Additiv** wie
     * `lizenz`: ein neues Feld bricht keinen Aufrufer. Kein `job_lauf` — die
     * Laufliste `sicherung_lauf` ist der Herzschlag (`D-19`).
     */
    datensicherung: datensicherungStatusSchema,
})
    .strict();
/** Eine Zeile der Fehlerablage — `GET /api/v1/system/fehler`. */
export const systemFehlerZeileSchema = z
    .object({
    id: uuid,
    occurredAt: z.string(),
    correlationId: uuid,
    method: z.string().nullable(),
    path: z.string().nullable(),
    statusCode: z.number().int(),
    errorName: z.string(),
    message: z.string(),
    stack: z.string().nullable(),
    actorId: uuid.nullable(),
    /**
     * Woher der Akteur kommt, oder `null` — `'user'` steht zusammen mit
     * `actorId`, `'token'` mit `tokenId`. Seit Migration `0111`; davor stand
     * ein über ein Bearer-Token ausgelöster Fehler als «kein Akteur» da, weil
     * `actorId` auf `app_user` verweist und ein Token dort nicht existiert.
     */
    actorKind: z.enum(['user', 'token']).nullable(),
    /** Gesetzt genau dann, wenn `actorKind === 'token'`. */
    tokenId: uuid.nullable(),
})
    .strict();
/**
 * Die Antworthülle von `GET /api/v1/system/fehler` — `{ zeilen: [...] }`. Der Umschlag lebt neben seinem
 * Elementschema, damit Server und Oberfläche dieselbe Hülle prüfen (Handbuch §3.969).
 */
export const systemFehlerListResponseSchema = z.object({ zeilen: z.array(systemFehlerZeileSchema).readonly() }).strict();
// ---------------------------------------------------------------------------
// Der Anfrage-Debugger — `GET /api/v1/system/anfragen` (Auftrag `W-debugger-ui`)
// ---------------------------------------------------------------------------
/**
 * Ein Akteur der Zugriffsspur, in der Form, in der ihn die Oberfläche zeigt.
 *
 * **Drei Zustände und nicht zwei**, weil `access_log.actor_kind` seit
 * Migration `0109` nullable und ohne `CHECK` ist: `'user'` (eine Sitzung),
 * `'token'` (ein Bearer-Token eines internen Systems) und `null` — die
 * Anfrage, die vor jeder Authentifizierung abgewiesen wurde
 * (`denyUnknownRoutes`) oder eine öffentliche Route traf. `'system'` steht im
 * Postgres-Enum, kommt in dieser Tabelle aber praktisch nie vor (kein
 * Hintergrundlauf protokolliert über die HTTP-Middleware) — der Vertrag führt
 * ihn trotzdem, weil die Spalte den Wert tragen KANN und ein Schema, das an
 * einer real möglichen Zeile durchfällt, die Debug-Ansicht ausgerechnet dann
 * leert, wenn etwas Ungewöhnliches passiert ist.
 */
export const systemAnfrageAkteurArtSchema = actorKindSchema;
/**
 * Eine Zeile der Zugriffsspur — `access_log`, angereichert um zwei Namen und
 * eine Ja/Nein-Auskunft.
 *
 * **`tokenBeschreibung`/`tokenPrefix` statt eines Tokenwerts.** Der Wert
 * selbst steht nirgends in der Datenbank (nur sein SHA-256-Abdruck) und darf
 * auch nirgends in einer Antwort stehen; was einen Administrator interessiert,
 * ist «welches Token war das» — und das beantworten Beschreibung und Präfix.
 *
 * **`hatKoerper` ist keine Bequemlichkeit, sondern die Zusage `E-580` in der
 * Liste.** Ein Erfolg (`statusCode < 400`) hinterlässt nach
 * `accessLogMiddleware` **keine** `access_log_body`-Zeile; die Liste sagt das
 * je Zeile, statt die Oberfläche raten oder für jede Zeile einen Detailabruf
 * absetzen zu lassen, der dann leer zurückkommt.
 */
export const systemAnfrageZeileSchema = z
    .object({
    /** `access_log.id` — `bigint`, deshalb als Zeichenkette (wie bei `AuditEntry.id`). */
    id: z.string(),
    occurredAt: z.string(),
    method: z.string(),
    path: z.string(),
    /** Die Abfrageparameter, getrennt vom Pfad gespeichert. `null`, wenn keine da waren. */
    query: z.record(z.string()).nullable(),
    statusCode: z.number().int(),
    durationMs: z.number().int(),
    ipAddress: z.string().nullable(),
    correlationId: uuid,
    actorKind: systemAnfrageAkteurArtSchema.nullable(),
    actorId: uuid.nullable(),
    /** Die Anmeldeadresse des handelnden Menschen, oder `null`. */
    actorEmail: z.string().nullable(),
    tokenId: uuid.nullable(),
    /** Die Beschreibung des Tokens — **nie** sein Wert. */
    tokenBeschreibung: z.string().nullable(),
    /** Die ersten Zeichen des Tokens, so wie sie beim Anlegen abgelegt wurden. */
    tokenPrefix: z.string().nullable(),
    /** Ob zu dieser Zeile ein Fehlkörper gespeichert wurde. Bei einem Erfolg **nie**. */
    hatKoerper: z.boolean(),
})
    .strict();
/**
 * Ein Token als Filterquelle — **einschliesslich widerrufener**.
 *
 * Ein widerrufenes Token verschwindet nicht aus der Zugriffsspur; die
 * historische Suche «was hat dieses Token damals getan» ist gerade nach dem
 * Widerruf die interessante. Die Liste steht deshalb im Vertrag dieser Antwort
 * und nicht hinter `apitoken:manage`: Die Debugger-Seite läuft mit
 * `system:read`, und eine Filterauswahl, die eine zweite Fähigkeit verlangte,
 * wäre auf ihr entweder leer oder ein zweiter Rechteweg.
 */
export const systemAnfrageTokenSchema = z
    .object({
    id: uuid,
    beschreibung: z.string(),
    prefix: z.string(),
    widerrufen: z.boolean(),
})
    .strict();
/**
 * Die Liste — **und sie sagt, was sie zählt.**
 *
 * `gesamt` ist die Zahl der Zeilen **nach** den gesetzten Filtern, nicht der
 * Bestand des Protokolls; `gesamtOhneFilter` steht daneben, dieselbe
 * Doppelangabe wie bei `incomingInvoiceListSchema` und aus demselben Grund
 * (Handbuch §3.132): Eine Zahl neben einem gesetzten Filter liest sich sonst
 * als Gesamtzahl und ist keine.
 */
export const systemAnfragenListeSchema = z
    .object({
    zeilen: z.array(systemAnfrageZeileSchema),
    gesamt: z.number().int().nonnegative(),
    gesamtOhneFilter: z.number().int().nonnegative(),
    /** Die Blättergrösse, die wirklich gegolten hat (nach Vorgabe und Deckel). */
    limit: z.number().int().positive(),
    offset: z.number().int().nonnegative(),
    tokens: z.array(systemAnfrageTokenSchema),
})
    .strict();
/**
 * Der Fehlkörper einer Zeile — `access_log_body`, so wie er gespeichert
 * wurde.
 *
 * **Die Oberfläche schwärzt nicht.** Was hier steht, hat `redactBody()`
 * (`apps/api/src/audit/access-log-body.ts`) bereits beim Schreiben geschwärzt;
 * ein Feld erscheint als die wörtliche Zeichenkette `"[geschwärzt]"`. Eine
 * zweite Schwärzung in der Anzeige verspräche einen Schutz, den sie nicht
 * leistet — der ungeschwärzte Wert existiert an dieser Stelle längst nicht
 * mehr.
 *
 * `requestBody`/`responseBody` sind `unknown`: Ein Anfragekörper ist
 * Fremdform, und ein Vertrag, der ihm eine Gestalt vorschriebe, liesse die
 * Detailansicht ausgerechnet an dem Körper durchfallen, den man ansehen will.
 */
export const systemAnfrageKoerperSchema = z
    .object({
    requestBody: z.unknown(),
    requestTruncated: z.boolean(),
    responseBody: z.unknown(),
    responseTruncated: z.boolean(),
})
    .strict();
/**
 * Die Detailansicht zu genau einer Zeile — `GET /api/v1/system/anfragen/:id`.
 *
 * Drei Quellen, eine `correlation_id`: die Zeile selbst (`access_log`), ihr
 * Fehlkörper (`access_log_body`, nur bei `statusCode >= 400` überhaupt
 * vorhanden) und der Systemfehler (`system_error`, nur bei `statusCode >= 500`
 * gesucht — `DatabaseErrorFilter` schreibt dort für jede Antwort ab 500 ausser
 * `503`).
 *
 * **Beide Zusatzfelder sind `null`, wenn nichts gespeichert wurde**, und
 * werden nie ersatzweise gefüllt: Ein Debugger, der einen Körper zeigt, den
 * niemand aufgezeichnet hat, ist keine Auskunft, sondern eine Erfindung
 * (`E-580`).
 */
export const systemAnfrageDetailSchema = z
    .object({
    zeile: systemAnfrageZeileSchema,
    koerper: systemAnfrageKoerperSchema.nullable(),
    systemfehler: systemFehlerZeileSchema.nullable(),
})
    .strict();
//# sourceMappingURL=system.js.map